import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { apiErrorCode } from './api-error';

// Every anonymous /auth/* endpoint (login, login-method, forgot/reset-password): attaching a stale/foreign
// Bearer token is pointless, and running the 401 handler below on a wrong-password/expired-reset-token
// attempt would log out and redirect away from a session that might still be valid in another tab.
// The invitation link's own calls (preview, redeem, decline by link) need no sign-in either (docs/design/39 section 6).
const ANONYMOUS_AUTH_PATHS = [
  '/auth/login',
  '/auth/login-method',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/invitations/preview',
  '/auth/invitations/redeem',
  '/auth/invitations/decline'
];
const isLoginRequest = (url: string) => ANONYMOUS_AUTH_PATHS.some((path) => url.endsWith(path));
// docs/design/38 section 9.1: the only call that takes the selection token (never the access token), and whose failure must not touch a stored session.
const isSelectRequest = (url: string) => url.endsWith('/auth/select-organization');
// docs/design/39 section 7.1: the selection token also reaches the invitations list and accept / decline (and nothing else).
const isInvitationRequest = (url: string) => url.includes('/auth/invitations');
const isOrganizationsRequest = (url: string) => url.endsWith('/auth/organizations');

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (isLoginRequest(req.url)) {
    return next(req);
  }

  // A request that names its own credential (the accept-invitation page acts as the account that signed in on that page) is never given another session's token.
  if (req.headers.has('Authorization')) {
    return next(req);
  }

  if (isSelectRequest(req.url) || (isInvitationRequest(req.url) && auth.selectionToken)) {
    const selectionToken = auth.selectionToken;
    const selectReq = selectionToken ? req.clone({ setHeaders: { Authorization: `Bearer ${selectionToken}` } }) : req;
    return next(selectReq).pipe(
      catchError((error) => {
        if (error.status === 401) {
          // The selection token expired (about five minutes): sign in again.
          auth.logout();
          router.navigate(['/login'], { replaceUrl: true });
        }
        return throwError(() => error);
      })
    );
  }

  const token = auth.accessToken;
  const authorizedReq = token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;

  return next(authorizedReq).pipe(
    catchError((error) => {
      if (error.status === 401) {
        // MEMBERSHIP_ROLES_CHANGED lands here too: the roles in the token are stale, so signing in again is the whole behavior.
        auth.logout();
        router.navigate(['/login'], { replaceUrl: true });
      }
      const code = error.status === 403 ? apiErrorCode(error) : null;
      if (code === 'MEMBERSHIP_INACTIVE' && !isOrganizationsRequest(req.url)) {
        // The active organization no longer accepts the person: offer the others (the current token still serves the switch endpoint), or sign out when there are none.
        auth.fetchOrganizations().subscribe({
          next: (list) => {
            if (list.length > 0) {
              auth.beginSwitchSelection(list);
              router.navigate(['/select-organization'], { replaceUrl: true });
            } else {
              auth.logout();
              router.navigate(['/login'], { replaceUrl: true });
            }
          },
          error: () => undefined
        });
      }
      if (code === 'ORGANIZATION_SELECTION_REQUIRED') {
        router.navigate([auth.hasPendingSelection() ? '/select-organization' : '/login'], { replaceUrl: true });
      }
      if (error.status === 403 && apiErrorCode(error) === 'ORGANIZATION_SUSPENDED') {
        // Behavior only (never text): the shell shows its own suspended notice from this flag.
        auth.markOrganizationSuspended();
      }
      return throwError(() => error);
    })
  );
};
